import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/global/components/ui/dialog'
import { cn } from '@/lib/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import React, { ReactNode, useEffect, useState } from 'react'
import {
    FormProvider,
    SubmitHandler,
    UseFormReturn,
    useForm,
} from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ZodEffects, ZodIntersection, ZodObject, ZodUnion } from 'zod'
import { useFocoDoTab } from './useFocoDoTab'
import { InternalProps } from './utils'

export interface RootContextProps {
    open?: boolean
    onClose?: () => void
    onChange?: (data: any) => void
    loading?: boolean
    onSubmit?: SubmitHandler<any>
    setInternalProps: (props: Partial<InternalProps>) => void
    methods: UseFormReturn<any> & {
        control: UseFormReturn<any>['control'] & {
            _options: UseFormReturn<any>['control']['_options'] & {
                disabled?: boolean
            }
        }
    }
}

interface RootProps
    extends Omit<
            React.ComponentProps<typeof DialogContent>,
            'open' | 'onSubmit' | 'onChange' | 'children'
        >,
        Omit<RootContextProps, 'methods' | 'setInternalProps'> {
    children?: ReactNode
    /**
     * `Esc` não fecha o diálogo — mesmo nome do prop que o MUI tinha. Padrão:
     * ligado quando o diálogo tem `Dialog.Content`.
     */
    disableEscapeKeyDown?: boolean
    /** Largura máxima fora do celular. No celular o diálogo é a tela toda. */
    width?: keyof typeof larguras
    schema?:
        | ZodObject<any>
        | ZodEffects<any>
        | ZodIntersection<any, any>
        | ZodUnion<any>
    disableFields?: boolean
    defaultValues?: any
    reValidateMode?: 'onSubmit' | 'onBlur' | 'onChange'
}

export type { RootProps as DialogRootProps }

/**
 * Larguras do diálogo, nos tokens do Tailwind. Fora do celular ele nunca
 * encosta na borda: a largura é a da tela menos 4rem, limitada pelo token.
 */
const larguras = {
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
    '6xl': 'sm:max-w-6xl',
    fit: 'sm:w-fit',
    full: 'sm:max-w-none',
} as const

export const RootContext = React.createContext<RootContextProps>(undefined!)

export function Root({
    children,
    open,
    onClose,
    className,
    loading,
    onSubmit,
    schema: zodSchema,
    defaultValues,
    onChange,
    disableFields,
    width = '2xl',
    reValidateMode = 'onSubmit',
    disableEscapeKeyDown,
    ...props
}: RootProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const focoDoTab = useFocoDoTab()

    open = open === undefined ? true : open
    onClose = onClose ?? (() => navigate(-1))
    const [internalProps, setInternalProps] = useState<InternalProps>({})

    const methods = useForm({
        defaultValues: transformData(defaultValues),
        resolver: async (data, context, options) => {
            if (zodSchema) {
                return zodResolver(
                    // @ts-ignore
                    zodSchema,
                )(transformData(data), context, options)
                    .then(result => {
                        console.log('data', data)
                        console.log('zod', result)

                        const errors = Object.keys(result.errors)

                        if (errors.length > 0) {
                            toast.error(
                                'Verifique os campos com erros: ' +
                                    errors.join(','),
                            )
                        }

                        return result
                    })
                    .catch(err => {
                        console.error('err', err)
                        toast.error('Há campos obrigatórios não preenchidos!')
                        return err
                    })
            }

            return { values: data, errors: {} }
        },
        reValidateMode,
    })

    const watch = methods.watch()
    const isSubmitSuccessful = methods.formState.isSubmitSuccessful

    methods.control._options = {
        ...methods.control._options,
        disabled: disableFields,
    } as any

    useEffect(() => onChange && onChange(watch), [watch, onChange])

    useEffect(() => {
        if (!open) {
            methods.reset()
        }
    }, [open])

    useEffect(() => {
        if (location.hash && open) {
            const fieldElement = document?.getElementsByName(
                location.hash.replace('#', ''),
            )?.[0]
            if (fieldElement) {
                setTimeout(() => {
                    fieldElement.scrollIntoView({ behavior: 'smooth' })
                    fieldElement.focus()
                }, 600)
            }
        }
    }, [location.hash, open])

    useEffect(() => {
        if (isSubmitSuccessful && internalProps.closeOnSubmit) {
            onClose?.()
        }
    }, [internalProps.closeOnSubmit, isSubmitSuccessful])

    const context = {
        open,
        onClose,
        loading,
        onSubmit,
        methods,
        setInternalProps: props => {
            setInternalProps(state => ({ ...state, ...props }))
        },
    } satisfies RootContextProps

    /**
     * O Radix reclama no console quando o diálogo não tem título. Boa parte
     * das confirmações de exclusão não usa `Dialog.Title` — nesses entra um
     * título só para leitor de tela.
     */
    const temTitulo = temFilho(children, 'isTituloDoDialogo')

    /**
     * Diálogo com conteúdo é formulário: fechar por tecla joga fora o que foi
     * digitado sem confirmação, então só botão fecha. `Backspace` entra junto
     * porque fora de um campo editável ele volta no histórico em alguns
     * navegadores — e as rotas de diálogo fecham no `navigate(-1)`.
     */
    const temConteudo = temFilho(children, 'isConteudoDoDialogo')
    const teclaNaoFecha = disableEscapeKeyDown ?? temConteudo

    return (
        <Dialog
            open={open}
            onOpenChange={aberto => {
                if (!aberto) onClose?.()
            }}
        >
            <DialogContent
                // A descrição é opcional aqui — sem isto o Radix avisa no
                // console por não achar uma.
                aria-describedby={undefined}
                showCloseButton={false}
                onEscapeKeyDown={evento => {
                    if (teclaNaoFecha) evento.preventDefault()
                }}
                onKeyDownCapture={evento => {
                    if (!teclaNaoFecha || evento.key !== 'Backspace') return
                    if (isCampoEditavel(evento.target)) return
                    evento.preventDefault()
                }}
                onBlur={focoDoTab.onBlur}
                onFocus={focoDoTab.onFocus}
                className={cn(
                    // No celular ocupa a tela inteira; de `sm` para cima vira
                    // caixa centralizada, tão alta quanto o conteúdo pedir e
                    // tão larga quanto `width` deixar.
                    'top-0 left-0 translate-x-0 translate-y-0',
                    'flex h-full max-h-screen w-full max-w-none flex-col',
                    'gap-0 rounded-none border-0 p-0',
                    'min-h-0 overflow-y-hidden bg-card',
                    'sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
                    'sm:h-auto sm:max-h-[calc(100vh-4rem)]',
                    'sm:w-[calc(100vw-4rem)] sm:rounded-lg sm:border',
                    larguras[width],
                    className,
                )}
                {...props}
            >
                {!temTitulo && (
                    <DialogTitle className="sr-only">Diálogo</DialogTitle>
                )}
                <RootContext.Provider value={context}>
                    <FormProvider {...methods}>{children}</FormProvider>
                </RootContext.Provider>
            </DialogContent>
        </Dialog>
    )
}

type MarcadorDeFilho = 'isTituloDoDialogo' | 'isConteudoDoDialogo'

function temFilho(children: ReactNode, marcador: MarcadorDeFilho) {
    return React.Children.toArray(children).some(
        filho =>
            React.isValidElement(filho) &&
            (filho.type as Partial<Record<MarcadorDeFilho, boolean>>)?.[
                marcador
            ] === true,
    )
}

/** Onde `Backspace` apaga texto em vez de acionar o navegador. */
function isCampoEditavel(alvo: EventTarget | null) {
    if (!(alvo instanceof HTMLElement)) return false
    if (alvo.isContentEditable) return true
    if (alvo instanceof HTMLTextAreaElement) return true
    return alvo instanceof HTMLInputElement && !alvo.readOnly
}

export function transformData(data = {}) {
    return Object.fromEntries(Object.entries(data).map(formatValue))
}

function formatValue([key, value]: [string, any]): [string, any] {
    const datetimeRegex =
        /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d{3})Z/
    const cpfRegex = /(\d{3})\.(\d{3})\.(\d{3})-(\d{2})/
    const pisRegex = /(\d{3})\.(\d{5})\.(\d{2})\.(\d{1})-(\d{1})/

    if (value instanceof File) {
        return [key, value]
    }

    if (Array.isArray(value)) {
        return [key, value]
    }

    if (dayjs.isDayjs(value)) {
        return [key, value.format('YYYY-MM-DD')]
    }

    if (value instanceof Date) {
        return [key, value.toJSON().split('T')[0]]
    }

    if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
            const array = value.map(item =>
                Object.fromEntries(Object.entries(item).map(formatValue)),
            )
            return [key, array]
        }

        return [key, Object.fromEntries(Object.entries(value).map(formatValue))]
    }

    if (typeof value === 'string' && datetimeRegex.test(value)) {
        return [key, value.split('T')[0]]
    }

    if (typeof value === 'string' && cpfRegex.test(value)) {
        return [key, value.replace(/\D/g, '')]
    }

    if (typeof value === 'string' && pisRegex.test(value)) {
        return [key, value.replace(/\D/g, '')]
    }

    if (typeof value === 'string' && value === 'true') {
        return [key, true]
    }

    if (typeof value === 'string' && value === 'S') {
        return [key, true]
    }

    if (typeof value === 'string' && value === 'false') {
        return [key, false]
    }

    if (typeof value === 'string' && value === 'N') {
        return [key, false]
    }

    return [key, value]
}
